/* ============================================================
   AI Chat Page — gofor360
   Bootstraps a conversation from sessionStorage / ?q= param,
   renders product cards with add-to-cart.
   ============================================================ */

(function () {
  'use strict';

  const root = document.querySelector('.ai-chat-page');
  if (!root) return;

  // ---- i18n ----
  const i18n = window.AII18n || {
    locale: 'it', isPrimary: true, t: () => '', url: (p) => p
  };
  const T = (k) => i18n.t(k);

  // ---- Lock viewport: measure theme header and set an exact CSS var ----
  function measureHeader() {
    // Try common header containers across themes
    const candidates = [
      'sticky-header',
      'site-header',
      'header.header',
      'header[role="banner"]',
      '.header',
      '#shopify-section-header',
      '.shopify-section-group-header-group'
    ];
    let h = 0;
    for (const sel of candidates) {
      const el = document.querySelector(sel);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.height > h && r.height < window.innerHeight) h = r.height;
      }
    }
    if (!h) h = 70;
    document.documentElement.style.setProperty('--ah-header-offset', h + 'px');
    document.documentElement.style.setProperty('--ah-header-offset-mobile', Math.min(h, 64) + 'px');
  }
  measureHeader();
  window.addEventListener('resize', measureHeader);
  // Re-measure once images/fonts settle
  setTimeout(measureHeader, 250);
  setTimeout(measureHeader, 800);
  // Also lock body scroll defensively
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';

  // Force inner scroll on messages (theme CSS sometimes wins specificity battles)
  const _messagesScrollEl = root.querySelector('[data-ai-chat-messages]');
  if (_messagesScrollEl) {
    _messagesScrollEl.style.setProperty('overflow-y', 'auto', 'important');
    _messagesScrollEl.style.setProperty('overflow-x', 'hidden', 'important');
    _messagesScrollEl.style.setProperty('flex', '1 1 auto', 'important');
    _messagesScrollEl.style.setProperty('min-height', '0', 'important');
    _messagesScrollEl.style.setProperty('-webkit-overflow-scrolling', 'touch');
    _messagesScrollEl.style.setProperty('touch-action', 'pan-y');
  }

  const endpoint = root.dataset.endpoint || '';
  const cartUrl = root.dataset.cartUrl || '/cart/add.js'; // Liquid emits routes.cart_add_url, already locale-aware
  const messagesEl = root.querySelector('[data-ai-chat-messages]');
  const form = root.querySelector('[data-ai-chat-form]');
  const input = root.querySelector('[data-ai-chat-input]');
  const resetBtn = root.querySelector('[data-ai-reset]');

  // Translate the static chrome on non-primary locales
  if (!i18n.isPrimary) {
    const backSpan = root.querySelector('.ai-chat-page__back span');
    if (backSpan) backSpan.textContent = T('backHome');
    const backLink = root.querySelector('.ai-chat-page__back');
    if (backLink) backLink.setAttribute('href', i18n.url('/'));

    const title = root.querySelector('.ai-chat-page__title');
    if (title) {
      // keep the pulsing dot, replace only the text node after it
      const bullet = title.querySelector('.ai-chat-page__bullet');
      title.textContent = '';
      if (bullet) title.appendChild(bullet);
      title.appendChild(document.createTextNode(' ' + T('assistantName')));
    }

    if (resetBtn) resetBtn.setAttribute('aria-label', T('resetChat'));
    if (input) input.placeholder = T('chatPlaceholder');

    const disc = root.querySelector('.ai-chat-page__disclaimer');
    if (disc) disc.textContent = T('disclaimer');
  }

  const history = [];

  // ---- bootstrap from ?q= or sessionStorage ----
  const params = new URLSearchParams(window.location.search);
  let firstMessage = params.get('q') || '';
  try {
    if (!firstMessage) firstMessage = sessionStorage.getItem('aiHeroFirstMessage') || '';
    sessionStorage.removeItem('aiHeroFirstMessage');
  } catch (e) {}

  if (firstMessage.trim()) {
    sendMessage(firstMessage.trim());
    // Clean URL so reload doesn't re-trigger
    if (params.get('q')) {
      params.delete('q');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  } else {
    // Greeting if user lands here cold
    appendMessage('ai', T('greeting'), []);
  }

  // ---- product autocomplete on the composer (opens upward) ----
  if (window.AIProductSearch && input) {
    window.AIProductSearch.attach(input, {
      anchor: form,
      onSelect: () => true
    });
    // The composer sits at the bottom → flip the dropdown above it
    const dd = form.querySelector('.ai-search-dropdown');
    if (dd) dd.classList.add('ai-search-dropdown--up');
  }

  // ---- form submit ----
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    sendMessage(text);
  });

  // ---- reset ----
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      history.length = 0;
      messagesEl.innerHTML = '';
      appendMessage('ai', T('resetDone'), []);
    });
  }

  // ---- send ----
  async function sendMessage(text) {
    appendMessage('user', text);
    history.push({ role: 'user', content: text });
    const typing = appendTyping();

    try {
      let data;
      if (!endpoint) {
        await wait(700);
        data = demoBackend(text);
      } else {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: history, locale: i18n.locale })
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        data = await res.json();
      }
      typing.remove();
      const reply = (data.reply || '').trim();
      appendMessage('ai', reply, data.products || []);
      history.push({ role: 'assistant', content: reply });
    } catch (err) {
      console.error('[ai-chat-page]', err);
      typing.remove();
      appendMessage('ai', T('errorGeneric'));
    }
  }

  // ---- DOM helpers ----
  function appendMessage(role, text, products) {
    const wrap = document.createElement('div');
    wrap.className = 'ai-hero__msg ai-hero__msg--' + (role === 'user' ? 'user' : 'ai');
    // Render simple markdown-ish: line breaks + **bold** + [text](url)
    wrap.innerHTML = renderMarkdownLite(text);

    if (products && products.length > 0) {
      const grid = document.createElement('div');
      grid.className = 'ai-product-grid';
      products.forEach((p) => grid.appendChild(productCard(p)));
      wrap.appendChild(grid);
    }

    messagesEl.appendChild(wrap);
    scrollToBottom();
    return wrap;
  }

  function appendTyping() {
    const el = document.createElement('div');
    el.className = 'ai-hero__msg ai-hero__msg--ai';
    el.innerHTML = '<span class="ai-hero__typing"><span></span><span></span><span></span></span>';
    messagesEl.appendChild(el);
    scrollToBottom();
    return el;
  }

  // Cache per prodotti già fetchati
  const productCache = new Map();

  async function fetchProductByHandle(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const res = await fetch(i18n.url('/products/' + handle + '.js'));
      if (!res.ok) return null;
      const data = await res.json();
      productCache.set(handle, data);
      return data;
    } catch (e) { return null; }
  }

  const MONEY_LOCALE = { it: 'it-IT', en: 'en-IE', de: 'de-DE', es: 'es-ES' };
  function formatPrice(cents) {
    const loc = MONEY_LOCALE[i18n.locale] || 'it-IT';
    return (cents / 100).toLocaleString(loc, { style: 'currency', currency: 'EUR' });
  }

  function productCard(p) {
    const card = document.createElement('div');
    card.className = 'ai-product-card';

    // If we only have a handle, hydrate from Shopify
    if (p.handle && (!p.title || !p.variantId)) {
      const placeholder = document.createElement('div');
      placeholder.className = 'ai-product-card__image';
      card.appendChild(placeholder);
      const titleEl = document.createElement('a');
      titleEl.className = 'ai-product-card__title';
      titleEl.href = i18n.url('/products/' + p.handle);
      titleEl.textContent = p.title || T('loading');
      card.appendChild(titleEl);
      const priceEl = document.createElement('p');
      priceEl.className = 'ai-product-card__price';
      card.appendChild(priceEl);
      const atc = document.createElement('button');
      atc.className = 'ai-product-card__atc';
      atc.type = 'button';
      atc.textContent = T('addToCart');
      atc.addEventListener('click', () => addToCart(p, atc));
      card.appendChild(atc);

      // Hydrate async
      fetchProductByHandle(p.handle).then((data) => {
        if (!data) return;
        p.title = data.title;
        p.variantId = data.variants?.[0]?.id;
        p.image = data.featured_image || data.images?.[0];
        p.price = formatPrice(data.price);
        p.compareAt = data.compare_at_price ? formatPrice(data.compare_at_price) : null;
        p.url = i18n.url('/products/' + data.handle);
        // Update DOM
        if (p.image) {
          placeholder.replaceWith(Object.assign(document.createElement('img'), {
            className: 'ai-product-card__image',
            src: p.image, alt: p.title, loading: 'lazy'
          }));
        } else {
          placeholder.remove();
        }
        titleEl.textContent = p.title;
        priceEl.innerHTML = (p.compareAt && p.compareAt !== p.price ? '<del>' + p.compareAt + '</del> ' : '') + p.price;
      });
      return card;
    }

    if (p.image && p.image.trim()) {
      const img = document.createElement('img');
      img.className = 'ai-product-card__image';
      img.src = p.image;
      img.alt = p.title || '';
      img.loading = 'lazy';
      img.onerror = () => img.remove();
      card.appendChild(img);
    }

    const title = document.createElement('a');
    title.className = 'ai-product-card__title';
    title.href = p.url || '#';
    title.textContent = p.title || 'Prodotto';
    card.appendChild(title);

    if (p.price) {
      const price = document.createElement('p');
      price.className = 'ai-product-card__price';
      price.innerHTML = (p.compareAt && p.compareAt !== p.price ? '<del>' + p.compareAt + '</del> ' : '') + p.price;
      card.appendChild(price);
    }

    const atc = document.createElement('button');
    atc.className = 'ai-product-card__atc';
    atc.type = 'button';
    atc.textContent = T('addToCart');
    atc.addEventListener('click', () => addToCart(p, atc));
    card.appendChild(atc);

    return card;
  }

  async function addToCart(product, button) {
    if (!product.variantId) {
      // Open product page if we don't know the variant yet
      window.location.href = product.url || '#';
      return;
    }
    button.setAttribute('aria-busy', 'true');
    button.textContent = T('adding');
    try {
      const res = await fetch(cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: product.variantId, quantity: 1 }] })
      });
      if (!res.ok) throw new Error('Cart error');
      // Notify the theme cart drawer if present
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      window.dispatchEvent(new CustomEvent('cart:updated'));
      button.classList.add('is-added');
      button.textContent = T('added');
      // Try to open the theme cart drawer (gofor360 uses cart-drawer)
      const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
      else if (cartDrawer) cartDrawer.classList.add('is-open');
    } catch (e) {
      console.error('[ai-chat-page] add to cart', e);
      button.textContent = T('retry');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  function scrollToBottom() {
    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    });
  }

  function wait(ms) { return new Promise((r) => setTimeout(r, ms)); }

  function renderMarkdownLite(text) {
    if (!text) return '';
    return escapeHtml(text)
      .replace(/\n/g, '<br>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  }

  function escapeHtml(s) {
    return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  // ---- demo backend (fallback when chat_endpoint not configured) ----
  function demoBackend(text) {
    const t = text.toLowerCase();
    let reply = '';
    let products = [];
    if (/dorm|sonno|insonn/.test(t)) {
      reply = 'Per il sonno consiglio **Relax** (la nostra miscela con melatonina e botanici per addormentarsi più facilmente) e **MAG 5+** (magnesio bisglicinato, ottimo per il rilassamento muscolare e nervoso). Per problemi più seri, c\'è anche il **Kit Sonno** che li unisce.\n\nDimmi: fatichi ad addormentarti o ti svegli durante la notte?';
      products = [{ handle: 'gofor360-relax' }, { handle: 'mag-5' }, { handle: 'kit-sonno' }];
    } else if (/energ|stanc|focus|concentr/.test(t)) {
      reply = 'Per energia e focus consiglio **Vitamina B** (complesso B per il metabolismo energetico) o **No Age** (formula completa antiossidante e mitocondriale). Se la stanchezza dura da settimane, vale la pena controllare ferro, B12 e vitamina D.';
      products = [{ handle: 'gofor360-vitamina-b' }, { handle: 'gofor360-noage' }];
    } else if (/magn/.test(t)) {
      reply = 'Abbiamo tre **MAG 5+**:\n• **MAG 5+ Bisglicinato** — la versione classica, ottima per stress, rilassamento, recupero\n• **MAG5+ Regolarità** — supporto al transito intestinale\n• **MAG5+ Recupero** — focus su muscoli e sport\n\nQuale obiettivo ti interessa di più?';
      products = [{ handle: 'mag-5' }, { handle: 'mag5-regolarita' }];
    } else if (/dolor|articol|infiamm/.test(t)) {
      reply = '**No Dolore** è il nostro antinfiammatorio naturale a base di curcuma, boswellia e zenzero per dolori articolari ricorrenti. Per dolore acuto o persistente, consulta sempre il medico.';
      products = [{ handle: 'gofor360-no-dolore' }];
    } else if (/immun|difese|raffreddore/.test(t)) {
      reply = '**Immuno** combina vitamina C, vitamina D, zinco e botanici per il supporto del sistema immunitario. Ottimo da iniziare in autunno e durante i periodi di stress.';
      products = [{ handle: 'gofor360-immuno' }];
    } else {
      reply = "Grazie! Per consigliarti il prodotto più adatto, configura l'endpoint backend AI nel theme editor. Per ora questa è una risposta demo.\n\nIntanto puoi dirmi: qual è il tuo obiettivo principale? (es. dormire meglio, più energia, dolori articolari, drenaggio...)";
    }
    return { reply, products };
  }
})();
